import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { supabase } from "../../lib/supabase";
import { ROUTES } from "../../lib/routes";
import { analytics } from "../../lib/analytics";
import { AccountForm } from "./forms/AccountForm";
import { HotelDetailsForm } from "./forms/HotelDetailsForm";
import { PMSCredentialsForm } from "./forms/PMSCredentialsForm";
import {
  safeBase64Encode,
  Connector,
  Credentials,
} from "../shared/ConnectorCredentialsForm";
import { CreateSftpAccountDto, SftpAccountDto } from "../../types/sftp";
import { OTPVerification } from "../shared/OTPVerification";
import { usePmsCredentialTest } from "../../hooks/usePmsCredentialTest";
import { withSyncedHotelDetailName } from "../../lib/hotelDetailName";
import { getDefaultPmsBaseUrl } from "../../lib/pmsDefaultBaseUrl";

// Helper function to generate a random 5-letter code
const generateHotelCode = (): string => {
  const letters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
  return Array.from({ length: 5 }, () =>
    letters.charAt(Math.floor(Math.random() * letters.length))
  ).join("");
};

export function HotelOnboarding() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [connectors, setConnectors] = useState<Connector[]>([]);
  const [selectedConnector, setSelectedConnector] = useState<Connector | null>(
    null
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [isExistingUser, setIsExistingUser] = useState(false);
  const [showOTPVerification, setShowOTPVerification] = useState(false);
  const [pendingUser, setPendingUser] = useState<any>(null);

  // Form state
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [hotelDetails, setHotelDetails] = useState({
    hotel_name: "",
    category: "",
    website: "",
    contact_phone: "",
    address: "",
    postal_code: "",
    city: "",
    country: "",
    registration_number: "",
  });
  const [credentials, setCredentials] = useState<Credentials>({});
  const [hotelCode, setHotelCode] = useState("");

  const {
    testLoading,
    testResult,
    testError,
    runDraftTest,
    clearTestResult,
  } = usePmsCredentialTest();

  useEffect(() => {
    if (currentStep === 2) {
      loadConnectors();
    }
  }, [currentStep]);

  const loadConnectors = async () => {
    try {
      setLoading(true);
      const { data, error: queryError } = await supabase
        .from("connectors")
        .select("id, code, name, type, status, cloudified, credentials")
        .eq("type", "pms")
        .eq("status", "active")
        .order("name");

      if (queryError) throw queryError;
      setConnectors(data || []);
    } catch (err) {
      console.error("Error loading connectors:", err);
      setError("Failed to load PMS providers");
    } finally {
      setLoading(false);
    }
  };

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep === 0) {
      navigate("/signup");
    } else if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleConnectorSelect = (connector: Connector) => {
    setSelectedConnector(connector);
    // Initialize credentials object with empty values for each required field.
    // Default is_primary to true since most hotels onboard with a single connector.
    const initialCredentials: Credentials = {
      is_primary: true,
    };

    // Only process credentials if they exist (might be null for cloudified connectors)
    if (connector.credentials && connector.credentials.length > 0) {
      connector.credentials.forEach((field) => {
        initialCredentials[field.key] = "";
      });
    }

    const defaultBaseUrl = getDefaultPmsBaseUrl(connector);
    if (defaultBaseUrl) {
      initialCredentials.base_url = defaultBaseUrl;
    }

    setCredentials(initialCredentials);
    clearTestResult();
  };

  const handleCredentialChange = (key: string, value: string | boolean) => {
    clearTestResult();
    setCredentials((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleTestConnection = () => {
    runDraftTest({
      connector: selectedConnector,
      credentials,
      hotelCode,
    });
  };

  // Helper kept commented while the SIRET validation is disabled — restore alongside the validation block in handleHotelCreation.
  // const validateSIRET = (siret: string): boolean => {
  //   return /^\d{14}$/.test(siret);
  // };

  const handleUserCreation = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setIsExistingUser(false);
    setLoading(true);

    try {
      // Validate required fields
      if (!firstName.trim() || !lastName.trim()) {
        throw new Error("First name and last name are required");
      }

      // Check if email is already registered
      const { data: existingProfile } = await supabase
        .from("profiles")
        .select("id")
        .eq("email", email)
        .maybeSingle();

      if (existingProfile) {
        setIsExistingUser(true);
        setError("An account with this email already exists.");
        return;
      }

      // Send OTP for email verification (this will create user if needed)
      const { data: otpData, error: otpError } = await supabase.auth.signInWithOtp({
        email,
        options: {
          shouldCreateUser: true,
        },
      });

      if (otpError) {
        if (otpError.message.includes("already registered") || otpError.message.includes("already been registered")) {
          setIsExistingUser(true);
          setError("An account with this email already exists.");
          return;
        }
        if (otpError.message.includes("For security purposes")) {
          setError("Please wait a moment before requesting another verification code.");
          return;
        }
        throw otpError;
      }

      // Store user data for after verification
      setPendingUser({
        email,
        password,
        firstName,
        lastName,
      });

      // Show OTP verification
      setShowOTPVerification(true);
      setError("");
    } catch (err: any) {
      console.error("Signup error:", err);
      setError(err.message || "An error occurred during signup");
    } finally {
      setLoading(false);
    }
  };

  const handleHotelCreation = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      // Registration number (SIRET) validation temporarily disabled — kept here so it can be restored later.
      // if (
      //   hotelDetails.country === "France" &&
      //   !validateSIRET(hotelDetails.registration_number)
      // ) {
      //   setError(
      //     "For France, registration number (SIRET) must be exactly 14 digits"
      //   );
      //   return;
      // }

      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error("No authenticated user found");

      // First check if a record already exists to handle the hotel_code properly
      const { data: existingHotel } = await supabase
        .from("hotel_details")
        .select("hotel_code")
        .eq("id", user.id)
        .maybeSingle();

      // Prepare hotel data
      const hotelData: any = {
        id: user.id, // Primary key - used for upserting
        ...withSyncedHotelDetailName(hotelDetails.hotel_name),
        category: hotelDetails.category,
        website: hotelDetails.website,
        address: hotelDetails.address,
        postal_code: hotelDetails.postal_code,
        city: hotelDetails.city,
        country: hotelDetails.country,
        registration_number: hotelDetails.registration_number?.trim() || null,
        contact_email: email,
        contact_phone: hotelDetails.contact_phone,
        updated_at: new Date().toISOString(),
      };

      // Add hotel_code - either use existing one or generate a new one
      if (existingHotel && existingHotel.hotel_code) {
        hotelData["hotel_code"] = existingHotel.hotel_code;
      } else {
        hotelData["hotel_code"] = generateHotelCode();
      }

      // Use upsert operation - insert if not exists, update if exists
      const { error: hotelError } = await supabase
        .from("hotel_details")
        .upsert(hotelData, {
          onConflict: "id",
          ignoreDuplicates: false,
        });

      if (hotelError) throw hotelError;
      setHotelCode(hotelData["hotel_code"]);
      // Move to next step
      handleNext();
    } catch (err: any) {
      console.error("Hotel details error:", err);
      setError(err.message || "Failed to save hotel details");
    } finally {
      setLoading(false);
    }
  };

  const handlePMSSetup = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error("No authenticated user found");

      if (!selectedConnector) {
        throw new Error("Please select a PMS provider");
      }

      // Always validate connector_hotel_id
      if (!credentials.connector_hotel_id) {
        throw new Error("Hotel Identifier is required");
      }
      console.log("Credentials data::::", credentials);
      // Check if the connector is cloudified
      if (selectedConnector.cloudified) {
        // For cloudified connectors, create SFTP account
        const hotelId =
          typeof credentials.connector_hotel_id === "string"
            ? credentials.connector_hotel_id
            : "";

        // Create SFTP account
        const createSftpDto: CreateSftpAccountDto = {
          hotelId: hotelCode.toLowerCase(),
        };

        // Get the API base URL from environment variables
        const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || "";

        // Call the API to create SFTP account
        const response = await fetch(`${apiBaseUrl}/v1/sftp/accounts`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(createSftpDto),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || "Failed to create SFTP account");
        }

        // Parse the response
        const sftpAccount: SftpAccountDto = await response.json();

        // Create connector_credentials JSON object with SFTP account details
        const connector_credentials = {
          sftp: {
            username: sftpAccount.username,
            password: sftpAccount.password,
            url: sftpAccount.url,
            port: sftpAccount.port,
          },
        };

        // Base64 encode the connector_credentials
        let encodedCredentials;
        try {
          encodedCredentials = safeBase64Encode(connector_credentials);
          console.log("Encoded SFTP credentials:", encodedCredentials);
        } catch (encodeError) {
          console.error("Error encoding SFTP credentials:", encodeError);
          throw new Error(
            "Failed to process SFTP credentials. Please try again."
          );
        }

        // First check if credentials already exist for this hotel and connector
        const { data: existingCredentials } = await supabase
          .from("credentials")
          .select("id")
          .eq("hotel_id", user.id)
          .eq("connector_type", "pms")
          .eq("connector_code", selectedConnector.code)
          .maybeSingle();

        // Prepare credential data for cloudified connector
        const credentialData = {
          hotel_id: user.id,
          connector_type: "pms",
          base_url: "", // No base_url for cloudified connectors
          connector_code: selectedConnector.code,
          connector_hotel_id: hotelId,
          is_primary: credentials.is_primary === true,
          reqParams: encodedCredentials,
          updated_at: new Date().toISOString(),
        };

        let credentialsError;

        if (existingCredentials) {
          // Update existing credentials
          const { error } = await supabase
            .from("credentials")
            .update(credentialData)
            .eq("id", existingCredentials.id);

          credentialsError = error;
        } else {
          // Insert new credentials
          const { error } = await supabase
            .from("credentials")
            .insert([credentialData]);

          credentialsError = error;
        }

        if (credentialsError) throw credentialsError;
      } else {
        // For non-cloudified connectors, follow the original flow
        // Validate base_url
        if (!credentials.base_url) {
          throw new Error("Connector URL is required");
        }

        // Validate URL format
        try {
          // Ensure base_url is a string before passing to URL constructor
          const baseUrl =
            typeof credentials.base_url === "string"
              ? credentials.base_url
              : "";
          new URL(baseUrl);
        } catch (e) {
          throw new Error("Please enter a valid Connector URL");
        }

        // Validate all required credentials are provided (if any)
        const missingFields =
          selectedConnector.credentials &&
          selectedConnector.credentials.length > 0
            ? selectedConnector.credentials
                .filter((field) => !credentials[field.key])
                .map((field) => field.key)
            : [];

        if (missingFields.length > 0) {
          throw new Error(
            `Missing required fields: ${missingFields.join(", ")}`
          );
        }

        // Create connector_credentials JSON object
        const connector_credentials = {
          credentials: { ...credentials }, // All the rest of the fields
        };

        // Remove the fields that are moved outside
        delete connector_credentials.credentials.connector_hotel_id;
        delete connector_credentials.credentials.base_url;
        delete connector_credentials.credentials.is_primary;

        console.log(
          "Connector credentials before encoding:",
          connector_credentials
        );

        // Base64 encode the connector_credentials using our safe encoding function
        let encodedCredentials;
        try {
          encodedCredentials = safeBase64Encode(connector_credentials);
          console.log("Encoded credentials:", encodedCredentials);
        } catch (encodeError) {
          console.error("Error encoding credentials:", encodeError);
          throw new Error("Failed to process credentials. Please try again.");
        }

        // First check if credentials already exist for this hotel and connector
        const { data: existingCredentials } = await supabase
          .from("credentials")
          .select("id")
          .eq("hotel_id", user.id)
          .eq("connector_type", "pms")
          .eq("connector_code", selectedConnector.code)
          .maybeSingle();

        // Prepare credential data
        const credentialData = {
          hotel_id: user.id,
          connector_type: "pms",
          base_url:
            typeof credentials.base_url === "string"
              ? credentials.base_url
              : "",
          connector_code: selectedConnector.code,
          connector_hotel_id:
            typeof credentials.connector_hotel_id === "string"
              ? credentials.connector_hotel_id
              : "",
          is_primary: credentials.is_primary === true,
          reqParams: encodedCredentials,
          updated_at: new Date().toISOString(),
        };

        let credentialsError;

        if (existingCredentials) {
          // Update existing credentials
          const { error } = await supabase
            .from("credentials")
            .update(credentialData)
            .eq("id", existingCredentials.id);

          credentialsError = error;
        } else {
          // Insert new credentials
          const { error } = await supabase
            .from("credentials")
            .insert([credentialData]);

          credentialsError = error;
        }

        if (credentialsError) throw credentialsError;
      }

      // Track hotel onboarding completion
      analytics.trackHotelOnboardingCompleted(user.id);

      // Navigate to hotel dashboard
      navigate(ROUTES.extranet("hotel"));
    } catch (err: any) {
      console.error("PMS setup error:", err);
      setError(err.message || "Failed to save Connector credentials");
    } finally {
      setLoading(false);
    }
  };

  const steps = [
    {
      title: "Create Your Account",
      description:
        "Set up your personal account to get started with our platform.",
      image:
        "https://images.unsplash.com/photo-1553877522-43269d4ea984?auto=format&fit=crop&q=80&w=2070",
    },
    {
      title: "Hotel Details",
      description:
        "Tell us about your hotel so we can better serve your needs.",
      image:
        "https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&q=80&w=2069",
    },
    {
      title: "Connector Integration",
      description:
        "Connect your Connector Management System (e.g. PMS) to enable seamless integration.",
      image:
        "https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&q=80&w=2070",
    },
  ];

  const handleOTPSuccess = async () => {
    if (!pendingUser) return;

    try {
      setLoading(true);
      setError("");

      // Get the current user (should be authenticated after OTP verification)
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      
      if (userError || !user) {
        throw new Error("Failed to get authenticated user");
      }

      // Set password for the user so they can login normally in the future
      if (pendingUser.password) {
        const { error: passwordError } = await supabase.auth.updateUser({
          password: pendingUser.password
        });
        
        if (passwordError) {
          console.warn("Failed to set password:", passwordError);
          // Don't throw error here as the user is already authenticated
        }
      }

      // Create profile
      const profileData = {
        id: user.id,
        email: pendingUser.email,
        user_type: "hotel",
        full_name: `${pendingUser.firstName} ${pendingUser.lastName}`,
      };
      
      const { error: profileError } = await supabase.from("profiles").insert([profileData]);

      if (profileError) throw profileError;

      // Clear pending user data and hide OTP verification
      setPendingUser(null);
      setShowOTPVerification(false);

      // Move to next step
      handleNext();
    } catch (err: any) {
      console.error("OTP success error:", err);
      setError(err.message || "An error occurred after verification");
    } finally {
      setLoading(false);
    }
  };

  const handleOTPError = (errorMessage: string) => {
    setError(errorMessage);
  };

  const handleOTPResend = () => {
    setError("");
  };

  return (
    <div className="min-h-screen flex flex-col md:flex-row">
      {/* Left side - Form */}
      <div className="w-full md:w-1/2 bg-white p-4 md:p-8 flex flex-col relative">
        <div className="flex-1">
          {/* Progress indicator */}
          <div className="mb-8">
            <div className="flex items-center justify-between">
              {steps.map((step, index) => (
                <div
                  key={index}
                  className={`flex items-center ${
                    index < steps.length - 1 ? "flex-1" : ""
                  }`}
                >
                  <div
                    className={`
                      w-8 h-8 rounded-full flex items-center justify-center
                      ${
                        index <= currentStep
                          ? "bg-indigo-600 text-white"
                          : "bg-gray-200 text-gray-500"
                      }
                    `}
                  >
                    {index + 1}
                  </div>
                  {index < steps.length - 1 && (
                    <div
                      className={`
                        flex-1 h-0.5 mx-4
                        ${index < currentStep ? "bg-indigo-600" : "bg-gray-200"}
                      `}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Step content */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              {steps[currentStep].title}
            </h2>
            <p className="text-gray-500">{steps[currentStep].description}</p>
          </div>

          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-600">{error}</p>
              {isExistingUser && (
                <p className="mt-2 text-sm text-gray-600">
                  Already have an account?{" "}
                  <Link
                    to="/login"
                    className="text-indigo-600 hover:text-indigo-500 font-medium"
                  >
                    Sign in here
                  </Link>
                </p>
              )}
            </div>
          )}

          {currentStep === 0 && !showOTPVerification && (
            <AccountForm
              email={email}
              setEmail={setEmail}
              password={password}
              setPassword={setPassword}
              firstName={firstName}
              setFirstName={setFirstName}
              lastName={lastName}
              setLastName={setLastName}
            />
          )}

          {currentStep === 0 && showOTPVerification && (
            <OTPVerification
              email={email}
              onSuccess={handleOTPSuccess}
              onError={handleOTPError}
              onResend={handleOTPResend}
              loading={loading}
            />
          )}

          {currentStep === 1 && (
            <HotelDetailsForm
              hotelDetails={hotelDetails}
              setHotelDetails={setHotelDetails}
            />
          )}

          {currentStep === 2 && (
            <PMSCredentialsForm
              connectors={connectors}
              selectedConnector={selectedConnector}
              credentials={credentials}
              onConnectorSelect={handleConnectorSelect}
              onCredentialChange={handleCredentialChange}
              hotelCode={hotelCode}
              testLoading={testLoading}
              testResult={testResult}
              testError={testError}
              onTestConnection={handleTestConnection}
            />
          )}

          {!showOTPVerification && (
            <div className="mt-8 flex justify-between">
              <button
                type="button"
                onClick={handleBack}
                className="btn-secondary"
              >
                Back
              </button>
              <button
                type="submit"
                disabled={loading}
                className="btn-primary"
                onClick={
                  currentStep === 0
                    ? handleUserCreation
                    : currentStep === 1
                    ? handleHotelCreation
                    : handlePMSSetup
                }
              >
                {currentStep < steps.length - 1 ? "Next" : "Complete Setup"}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Right side - Background Image */}
      <div className="hidden md:block md:w-1/2 relative">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${steps[currentStep].image})` }}
        />
      </div>
    </div>
  );
}
